import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { openSuccessNotification, openErrorNotification } from '@/notification';
import { dashboardApi } from '@/pages/dashboard/dashboard.reducer';
import { pdfOperationError } from '@/common/pdf-operation-query';

const getInitialCartState = (cartName) => {
    try {
        return JSON.parse(sessionStorage.getItem(cartName)) || [];
    } catch {
        return [];
    }
};

const initialState = {
    dashboardCartDocumentIds: getInitialCartState("dashboardCartDocumentIds"),
    approvalCartDocumentIds: getInitialCartState("approvalCartDocumentIds"),
    emailFilter: {
        pdfFiles: false,
        originalFiles: false,
        folderStructure: false
    },
    emailResult: null
};

export const sendEmailWithDocuments = createAsyncThunk(
    'cartManagement/sendEmailWithDocuments',
    async (model, { dispatch, rejectWithValue }) => {
        try {
            const data = await dispatch(dashboardApi.endpoints.sendDocumentsOnEmail.initiate(model)).unwrap();
            openSuccessNotification(data);
            return data;
        } catch (error) {
            if (!error.response || (error.response && error.response.status !== 401)) {
                openErrorNotification(pdfOperationError(error, `Error. Documents aren't sent`));
            }
            return rejectWithValue(error.message);
        }
    }
);

export const downloadCartDocuments = createAsyncThunk(
    'cartManagement/downloadCartDocuments',
    async (model, { dispatch, rejectWithValue }) => {
        try {
            await dispatch(dashboardApi.endpoints.downloadCartFiles.initiate(model)).unwrap();
            return true;
        } catch (error) {
            if (!error?.response || (error?.response && error?.response.status !== 401)) {
                openErrorNotification(`Error. Can't download files`);
            }
            return rejectWithValue(error?.message || 'Failed');
        }
    }
);

export const clearCartAsync = createAsyncThunk(
    'cartManagement/clearCartAsync',
    async (cardDocumentsName, { getState }) => {
        const state = getState();
        const existingData = state.cartManagement[cardDocumentsName] || [];
        
        if (!existingData.length) {
            return cardDocumentsName;
        }
        
        openSuccessNotification(`Cart Management is empty`);
        return cardDocumentsName;
    }
);

const cartManagementSlice = createSlice({
    name: 'cartManagement',
    initialState,
    reducers: {
        addToCart: (state, action) => {
            const { cardDocumentsName, ids } = action.payload;
            const newIds = Array.isArray(ids) ? ids : [ids];
            
            if (!state[cardDocumentsName]) {
                state[cardDocumentsName] = [];
            }
            
            newIds.forEach(id => {
                if (!state[cardDocumentsName].includes(id)) {
                    state[cardDocumentsName].push(id);
                }
            });
        },
        removeFromCart: (state, action) => {
            const { cardDocumentsName, ids } = action.payload;
            const removeIds = Array.isArray(ids) ? ids : [ids];
            
            if (state[cardDocumentsName]) {
                state[cardDocumentsName] = state[cardDocumentsName].filter(
                    id => !removeIds.includes(id)
                );
            }
        },
        clearCart: (state, action) => {
            const { cardDocumentsName } = action.payload;
            state[cardDocumentsName] = [];
        },
        setCartItems: (state, action) => {
            const { cardDocumentsName, ids } = action.payload;
            const newIds = Array.isArray(ids) ? ids : [ids];
            state[cardDocumentsName] = [...new Set(newIds)];
        },
        setEmailFilter: (state, action) => {
            const { key, value } = action.payload;
            state.emailFilter[key] = value;
        },
        clearEmailFilter: (state) => {
            state.emailFilter = {
                pdfFiles: false,
                originalFiles: false,
                folderStructure: false
            };
            state.emailResult = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(clearCartAsync.fulfilled, (state, action) => {
                const cardDocumentsName = action.payload;
                state[cardDocumentsName] = [];
            })
                .addCase(sendEmailWithDocuments.pending, (state) => {
                    state.emailResult = null;
                })
                .addCase(sendEmailWithDocuments.fulfilled, (state, action) => {
                    state.emailResult = { status: 'success', message: action.payload };
                })
                .addCase(sendEmailWithDocuments.rejected, (state) => {
                    state.emailResult = { status: 'error', message: `Error. Documents aren't sent` };
                })
                .addCase(downloadCartDocuments.pending, () => undefined)
                .addCase(downloadCartDocuments.fulfilled, () => undefined)
                .addCase(downloadCartDocuments.rejected, () => undefined);
    }
});

export const {
    addToCart,
    removeFromCart,
    clearCart,
    setCartItems,
    setEmailFilter,
    clearEmailFilter
} = cartManagementSlice.actions;

export const selectCartItems = (state, cartName) => state.cartManagement[cartName] || [];
export const selectDashboardCartItems = (state) => state.cartManagement.dashboardCartDocumentIds || [];
export const selectApprovalCartItems = (state) => state.cartManagement.approvalCartDocumentIds || [];
export const selectEmailFilter = (state) => state.cartManagement.emailFilter;
export const selectEmailResult = (state) => state.cartManagement.emailResult;

export const addValue = (id, cardDocumentsName) => 
    addToCart({ cardDocumentsName, ids: id });

export const removeValue = (id, cardDocumentsName) => 
    removeFromCart({ cardDocumentsName, ids: id });

export const deleteFromCart = (ids, cardDocumentsName) => 
    removeFromCart({ cardDocumentsName, ids });

export const changeEmailFilter = (key, value) => 
    setEmailFilter({ key, value });

export const cartPersistenceMiddleware = (store) => (next) => (action) => {
    const result = next(action);
    
    if (action.type?.startsWith('cartManagement/')) {
        const state = store.getState();
        const { cartManagement } = state;
        const persistCart = (key, value) => {
            try {
                if (Array.isArray(value) && value.length > 0) {
                    sessionStorage.setItem(key, JSON.stringify(value));
                } else {
                    sessionStorage.removeItem(key);
                }
            } catch {
                // Intentionally ignore sessionStorage errors (e.g., quota exceeded or unavailable)
            }
        };

        persistCart('dashboardCartDocumentIds', cartManagement.dashboardCartDocumentIds);
        persistCart('approvalCartDocumentIds', cartManagement.approvalCartDocumentIds);
    }
    
    return result;
};

export default cartManagementSlice.reducer;
